import { type ReactNode, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Paragraph } from '../../Typography/Paragraph';
import ClickedOutside from '../../../hooks/ClickedOutside';
import { colors } from '../../../themes/stena-recycling/colors';
import { SimpleTooltip } from '../../Data/Tooltip/SimpleTooltip';
import {
  FileUploadContainer,
  FileUploadErrorText,
  FileUploadLabel,
  OuterLabelWrapper,
  TooltipWrapper,
} from './style';
import { FileUploadRowsContainer } from './components/FileUploadRowsContainer/FileUploadRowsContainer';
import { FileUploadBox } from './components/FileUploadBox/FileUploadBox';

type FileUploadProps = {
  uploadStatus: Record<string, UploadStatus>;
  filesState: FileUploadObj[];
  onChange: (files: FileUploadObj[]) => void;
  onFileAdded: (fileObj: FileUploadObj) => void;
  description?: string;
  label?: string;
  maxFileSizeMb?: number;
  maxFiles?: number;
  imagesOnly?: boolean;
  tooltipContent?: ReactNode;
};
export type FileUploadObj = { id: string; file: File };
export type UploadStatus = 'uploading' | 'completed' | 'failed';

const allowedFileUploadFiles = [
  {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    fileEnding: '.xlsx',
    readable: 'XLSX',
  },
  {
    type: 'text/csv',
    fileEnding: '.csv',
    readable: 'CSV',
  },
  {
    type: 'image/jpeg',
    fileEnding: '.jpg',
    readable: 'JPG',
  },
  {
    type: 'image/jpeg',
    fileEnding: '.jpeg',
    readable: 'JPEG',
  },
  {
    type: 'image/png',
    fileEnding: '.png',
    readable: 'PNG',
  },
  {
    type: 'application/pdf',
    fileEnding: '.pdf',
    readable: 'PDF',
  },
];

export const getAllowedFileTypes = (imagesOnly: boolean) => {
  return imagesOnly
    ? allowedFileUploadFiles.filter((file) => file.type.startsWith('image/'))
    : allowedFileUploadFiles;
};

export const FileUpload = ({
  uploadStatus,
  label,
  filesState,
  onChange,
  onFileAdded,
  description,
  maxFileSizeMb = 20,
  maxFiles = 5,
  imagesOnly = false,
  tooltipContent,
}: FileUploadProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const hasFiles = filesState.length > 0;

  const handleRemoveFile = (inputFileId: string) => {
    onChange(filesState.filter(({ id }) => id !== inputFileId));
  };

  const allowedFileTypes = useMemo(
    () => getAllowedFileTypes(imagesOnly).map((file) => file.type),
    [imagesOnly],
  );

  const handleFileChange = (files: File[]) => {
    setError(null);
    if ([...files, ...filesState].length > maxFiles) {
      setError(`${t('component.fileupload.limit.exceeded')} (${maxFiles}/${maxFiles})`);

      return;
    }

    const totalSizeInput = files.reduce((acc, fileObj) => acc + fileObj.size, 0);
    const totalSizeState = filesState.reduce((acc, fileObj) => acc + fileObj.file.size, 0);
    const totalSize = totalSizeInput + totalSizeState;
    const totalSizeMb = totalSize / 1000 / 1000;

    if (totalSizeMb > maxFileSizeMb) {
      setError(`${t('component.fileupload.size.exceeded')} (${maxFileSizeMb}MB)`);

      return;
    }

    const invalidFile = files.find((file) => !allowedFileTypes.includes(file.type));

    if (invalidFile) {
      setError(`${t('component.fileupload.type.invalid')}: ${invalidFile.name}`);

      return;
    }

    const newFiles = files.map((file) => ({ id: uuidv4(), file }));
    const allFiles = [...filesState, ...newFiles];

    onChange(allFiles);
    newFiles.forEach(onFileAdded);
  };

  ClickedOutside(wrapperRef, () => {
    setError(null);
  });

  return (
    <FileUploadContainer>
      <OuterLabelWrapper>
        {label && <FileUploadLabel>{label}</FileUploadLabel>}
        {tooltipContent && (
          <TooltipWrapper>
            <SimpleTooltip>{tooltipContent}</SimpleTooltip>
          </TooltipWrapper>
        )}
      </OuterLabelWrapper>

      <FileUploadBox
        handleFileChange={handleFileChange}
        wrapperRef={wrapperRef}
        error={error}
        filesState={filesState}
        maxFiles={maxFiles}
        fileInputRef={fileInputRef}
        imagesOnly={imagesOnly}
        maxFileSizeMb={maxFileSizeMb}
      />

      {error && <FileUploadErrorText>{error}</FileUploadErrorText>}
      {description && (
        <Paragraph variant="medium" color={colors.primary.grey} mt="tiny">
          {description}
        </Paragraph>
      )}

      {hasFiles && (
        <FileUploadRowsContainer
          files={filesState}
          uploadStatus={uploadStatus}
          handleRemoveFile={handleRemoveFile}
        />
      )}
    </FileUploadContainer>
  );
};
